import { mergeAttributes, Node, textblockTypeInputRule } from "@tiptap/core";
import { VueNodeViewRenderer } from "@tiptap/vue-3";
import Component from "./Node.vue";
import { createDefaultBlockForTipTap, createHeadingBlock } from "~/stores/mail-block-editor/defaults";
import type { HeadingBlock, Pixels } from "~/types/MailBlocks";

export type Level = HeadingBlock['level'];

export interface HeadingOptions {
    levels: Level[],
    fontSize?: Pixels
    HTMLAttributes: Omit<HeadingBlock, 'type' | 'children'>,
}

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        heading: {
            /**
             * Set a heading node
             * @param attributes The heading attributes
             * @example editor.commands.setHeading({ level: 1 })
             */
            setHeading: (attributes: Omit<HeadingOptions, 'HTMLAttributes'>) => ReturnType,
            /**
             * Toggle a heading node
             * @param attributes The heading attributes
             * @example editor.commands.toggleHeading({ level: 1 })
             */
            toggleHeading: (attributes: Omit<HeadingOptions, 'HTMLAttributes'>) => ReturnType,
        }
    }
}

const Heading = Node.create({
    name: "heading",
    group: "block",
    content: "inline*",
    defining: true,

    addOptions() {
        return {
            levels: [1, 2, 3, 4, 5, 6],
            fontSize: undefined,
            HTMLAttributes: {},
        }
    },

    addAttributes() {
        return createDefaultBlockForTipTap("heading");
    },

    parseHTML() {
        return this.options.levels
            .map((level: Level) => ({
                tag: `h${level}`,
                attrs: { level },
            }))
    },

    renderHTML({ node, HTMLAttributes }) {
        const hasLevel = this.options.levels.includes(node.attrs.level)
        const level = hasLevel
            ? node.attrs.level
            : this.options.levels[0]

        return [`h${level}`, mergeAttributes(this.options.HTMLAttributes, HTMLAttributes), 0]
    },

    addNodeView() {
        return VueNodeViewRenderer(Component);
    },

    addCommands() {
        return {
            setHeading: attributes => ({ commands }) => {
                if (!this.options.levels.includes(attributes.level)) {
                    return false
                }

                if (!("fontSize" in attributes)) {
                    attributes.fontSize = createHeadingBlock(attributes).attrs.fontSize;
                }

                return commands.setNode(this.name, attributes)
            },
            toggleHeading: attributes => ({ commands }) => {
                if (!this.options.levels.includes(attributes.level)) {
                    return false
                }

                const store = useMailStore();

                if (!("fontSize" in attributes)) {
                    attributes.fontSize = createHeadingBlock(attributes).fontSize as Pixels;
                }

                if (store.currentBlock) {
                    attributes = { ...store.currentBlock.attrs, ...attributes };
                }

                return commands.toggleNode(this.name, 'heading', attributes)
            },
        }
    },

    addKeyboardShortcuts() {
        return this.options.levels.reduce((items: any[], level: Level) => ({
            ...items,

            [`Mod-Alt-${level}`]: () => this.editor.commands.toggleHeading({ level }),
        }), {})
    },

    addInputRules() {
        return this.options.levels.map(level => {
            return textblockTypeInputRule({
                find: new RegExp(`^(#{1,${level}})\\s$`),
                type: this.type,
                getAttributes: {
                    level,
                },
            })
        })
    },
});

export default Heading;
